import { registerSW as register } from 'virtual:pwa-register';

/**
 * Service worker registration with auto-update + user-prompted reload.
 *
 * Background:
 *   - vite-plugin-pwa is configured with `registerType: 'autoUpdate'`, which
 *     means Workbox calls `skipWaiting()` + `clientsClaim()` automatically.
 *     The new SW takes control on next page load, but JS chunks already
 *     loaded in memory are NOT replaced — the running app keeps its old code.
 *   - That bit us in v1.0.1: the AdminShell bulk-import bug fix shipped in
 *     a new bundle, but users with the tab open kept running the old
 *     AdminShell from the workbox precache and saw the "fix didn't work."
 *
 * Solution:
 *   - When Workbox detects a new SW waiting (`onNeedRefresh`), publish that
 *     state via the `__scandeckUpdateAvailable` event. The <UpdateToast />
 *     component listens and shows a "New version available — Reload" bar.
 *   - Force a hard fetch of `/sw.js` every 60s so users who keep the tab
 *     open for hours still see the update prompt within a minute of deploy.
 *
 * No external deps; the toast subscribes to a CustomEvent on `window`.
 */

const UPDATE_AVAILABLE_EVENT = 'scandeck:update-available';
const UPDATE_CHECK_INTERVAL_MS = 60_000;

declare global {
	interface WindowEventMap {
		'scandeck:update-available': CustomEvent<void>;
	}
	interface Window {
		__scandeckUpdateSW?: () => Promise<void>;
		__scandeckCheckUpdate?: () => Promise<void>;
	}
}

export function registerSW(): void {
	let updateSWFn: ((reloadPage?: boolean) => Promise<void>) | undefined;
	let pollHandle: ReturnType<typeof setInterval> | undefined;

	// One-shot stale-chunk unstick (v1.0.3):
	//
	// Users coming from v1.0.0/v1.0.1/v1.0.2 (which used registerType:'autoUpdate')
	// have a service worker that silently activated, but the JS chunks already
	// in their tab's memory are still old — so AdminShell, etc., behave like the
	// old broken version even though the version chip shows the new tag.
	//
	// `controllerchange` fires when a new SW takes control of this client. If
	// it fires AFTER the page has already loaded (i.e. while the user is using
	// the app), we know we're now talking to a SW that doesn't match the JS
	// chunks in memory. Force exactly one reload so we get back in sync.
	if ('serviceWorker' in navigator) {
		let reloading = false;
		navigator.serviceWorker.addEventListener('controllerchange', () => {
			if (reloading) return;
			reloading = true;
			if (import.meta.env.DEV) console.info('[pwa] SW controller changed — reloading to sync chunks');
			window.location.reload();
		});
	}

	const updateSW = register({
		onNeedRefresh() {
			if (import.meta.env.DEV) console.info('[pwa] new version available — prompting user');
			window.dispatchEvent(new CustomEvent(UPDATE_AVAILABLE_EVENT));
		},
		onOfflineReady() {
			if (import.meta.env.DEV) console.info('[pwa] offline shell ready');
		},
		onRegisteredSW(swUrl, registration) {
			if (import.meta.env.DEV) console.info('[pwa] sw registered at', swUrl);
			// Periodically ping Workbox to recheck for an update. Workbox's
			// default behavior only checks on navigation, which means a user
			// who leaves the tab open all day never sees a deploy until they
			// refresh. 60s is aggressive but cheap (one HEAD on /sw.js).
			if (registration && !pollHandle) {
				pollHandle = setInterval(() => {
					void registration.update().catch((err) => {
						console.warn('[pwa] sw update check failed', err);
					});
				}, UPDATE_CHECK_INTERVAL_MS);
			}
		},
		onRegisterError(err) {
			console.warn('[pwa] sw registration failed', err);
		},
	});

	updateSWFn = updateSW;

	// Force-update entry point used by the toast's "Reload" button.
	window.__scandeckUpdateSW = () => updateSWFn?.(true) ?? Promise.resolve();
	// Manual trigger for the dev console / smoke-test flows.
	window.__scandeckCheckUpdate = async () => {
		const reg = await navigator.serviceWorker.getRegistration();
		if (reg) await reg.update();
	};
}
